import styled from "styled-components";
import { MANENO_TYPE, MANENO_SPACE, MANENO_RADIUS, MANENO_SHADOW } from "../manenoTheme";

export const StyledManenoLayout = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: var(--color-page-bg);
  font-family: var(--font-body);

  /* ── Top header ───────────────────────────────────────── */
  .maneno-header {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: ${MANENO_SPACE.headerHeight};
    background: var(--color-header-bg);
    border-bottom: 1px solid var(--color-header-border);
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 24px 0 0;
    z-index: 100;
    box-shadow: var(--shadow-header);
  }

  .header-brand {
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${MANENO_SPACE.sidebarWidth};
    height: 100%;
    background: var(--color-sidebar-bg);
    flex-shrink: 0;
    text-decoration: none;
    cursor: pointer;
    border: none;
    padding: 0 18px;
    gap: 8px;
  }

  .maneno-brand-icon {
    width: 28px;
    height: 28px;
    background: var(--color-primary);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: ${MANENO_TYPE.weightBlack};
    font-size: 15px;
    color: #fff;
    flex-shrink: 0;
    line-height: 1;
  }

  .maneno-logo-text {
    font-family: var(--font-heading);
    font-weight: ${MANENO_TYPE.logoWeight};
    font-size: ${MANENO_TYPE.logoSize};
    color: #fff;
    letter-spacing: -0.02em;
    line-height: 1;
    user-select: none;
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 16px;
    flex: 1;
    justify-content: flex-end;
  }

  .header-user-pill {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px 4px 4px;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-pill);
    cursor: pointer;
    transition: background 0.15s;
    background: transparent;

    &:hover {
      background: var(--color-page-bg);
    }
  }

  .header-avatar {
    width: 28px;
    height: 28px;
    background: var(--color-primary);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: ${MANENO_TYPE.weightBold};
    font-size: 13px;
    color: #fff;
    flex-shrink: 0;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .header-username {
    font-size: ${MANENO_TYPE.body};
    font-weight: ${MANENO_TYPE.weightMd};
    color: var(--color-ink);
    white-space: nowrap;
  }

  .header-chevron {
    color: var(--color-ink-muted);
    font-size: 10px;
  }

  .header-logout-btn {
    background: transparent;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-button);
    padding: 4px 12px;
    font-family: var(--font-body);
    font-size: ${MANENO_TYPE.body};
    font-weight: ${MANENO_TYPE.weightMd};
    color: var(--color-ink-muted);
    cursor: pointer;
    transition: all 0.15s;

    &:hover {
      background: var(--color-page-bg);
      border-color: var(--color-ink);
      color: var(--color-ink);
    }
  }

  /* ── Body (sidebar + content) ─────────────────────────── */
  .maneno-body {
    display: flex;
    padding-top: ${MANENO_SPACE.headerHeight};
    min-height: 100vh;
  }

  /* ── Sidebar ──────────────────────────────────────────── */
  .maneno-sidebar {
    position: fixed;
    top: ${MANENO_SPACE.headerHeight};
    left: 0;
    width: ${MANENO_SPACE.sidebarWidth};
    height: calc(100vh - ${MANENO_SPACE.headerHeight});
    background: var(--color-sidebar-bg);
    display: flex;
    flex-direction: column;
    padding: 12px 0 20px;
    z-index: 99;
    overflow-y: auto;
    overflow-x: hidden;
  }

  .sidebar-section-label {
    font-size: ${MANENO_TYPE.xsmall};
    font-weight: ${MANENO_TYPE.weightSemi};
    color: var(--color-sidebar-text);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    padding: 0 20px;
    margin: 16px 0 6px;
    opacity: 0.6;
  }

  .sidebar-nav {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 1px;
    padding: 0 10px;
  }

  .sidebar-nav-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    border-radius: var(--radius-button);
    cursor: pointer;
    text-decoration: none;
    transition: background 0.15s;
    color: var(--color-sidebar-text);

    svg,
    img {
      flex-shrink: 0;
      width: 20px;
      height: 20px;
      transition: opacity 0.15s, color 0.15s, filter 0.15s;
    }

    svg {
      color: var(--color-sidebar-text);
      opacity: 0.7;
    }

    img {
      opacity: 0.75;
      filter: brightness(1.4);
    }

    .nav-label {
      font-size: ${MANENO_TYPE.body};
      font-weight: ${MANENO_TYPE.weightMd};
      color: var(--color-sidebar-text);
      white-space: nowrap;
      transition: color 0.15s;
    }

    &:hover {
      background: var(--color-sidebar-hover);

      svg {
        opacity: 1;
        color: #fff;
      }

      img {
        opacity: 1;
        filter: brightness(0) invert(1);
      }

      .nav-label {
        color: #fff;
      }
    }

    &.active {
      background: var(--color-sidebar-active-bg);

      svg {
        opacity: 1;
        color: #fff;
      }

      img {
        opacity: 1;
        filter: brightness(0) invert(1);
      }

      .nav-label {
        color: var(--color-sidebar-text-active);
        font-weight: ${MANENO_TYPE.weightSemi};
      }
    }
  }

  .sidebar-nav-item.active {
    position: relative;

    &::before {
      content: "";
      position: absolute;
      left: 0;
      top: 4px;
      bottom: 4px;
      width: 3px;
      background: var(--color-primary);
      border-radius: 0 2px 2px 0;
    }
  }

  .sidebar-divider {
    height: 1px;
    background: rgba(255, 255, 255, 0.07);
    margin: 12px 16px;
  }

  /* ── Submenu group ── */
  .sidebar-nav-group {
    display: flex;
    flex-direction: column;
  }

  .sidebar-nav-group-trigger {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    border-radius: var(--radius-button);
    cursor: pointer;
    text-decoration: none;
    transition: background 0.15s;
    color: var(--color-sidebar-text);
    background: none;
    border: none;
    width: 100%;
    text-align: left;
    font-family: inherit;

    svg,
    img {
      flex-shrink: 0;
      width: 20px;
      height: 20px;
      transition: opacity 0.15s, color 0.15s, filter 0.15s;
    }

    svg {
      color: var(--color-sidebar-text);
      opacity: 0.7;
    }

    img {
      opacity: 0.75;
      filter: brightness(1.4);
    }

    .nav-label {
      font-size: ${MANENO_TYPE.body};
      font-weight: ${MANENO_TYPE.weightMd};
      color: var(--color-sidebar-text);
      white-space: nowrap;
      flex: 1;
    }

    .nav-chevron {
      font-size: 10px;
      color: var(--color-sidebar-text);
      opacity: 0.5;
      transition: transform 0.2s;
      margin-left: auto;
    }

    &.open .nav-chevron {
      transform: rotate(180deg);
    }

    &:hover,
    &.open {
      background: var(--color-sidebar-hover);

      svg {
        opacity: 1;
        color: #fff;
      }

      img {
        opacity: 1;
        filter: brightness(0) invert(1);
      }

      .nav-label {
        color: #fff;
      }
      .nav-chevron {
        opacity: 0.8;
        color: #fff;
      }
    }
  }

  .sidebar-submenu {
    display: flex;
    flex-direction: column;
    gap: 1px;
    padding: 2px 0 4px 0;
    overflow: hidden;
    max-height: 0;
    transition: max-height 0.2s ease;

    &.open {
      max-height: 200px;
    }
  }

  .sidebar-submenu-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 7px 12px 7px 36px;
    border-radius: var(--radius-button);
    font-size: 12px;
    font-weight: ${MANENO_TYPE.weightMd};
    color: var(--color-sidebar-text);
    text-decoration: none;
    transition: background 0.15s;
    white-space: nowrap;
    opacity: 0.85;

    &:hover {
      background: var(--color-sidebar-hover);
      color: #fff;
      opacity: 1;
    }

    &.active {
      background: var(--color-sidebar-active-bg);
      color: var(--color-sidebar-text-active);
      font-weight: ${MANENO_TYPE.weightSemi};
      opacity: 1;
    }
  }

  .sidebar-footer {
    padding: 0 10px;
    margin-top: auto;
  }

  /* ── Main content area ────────────────────────────────── */
  .maneno-content {
    margin-left: ${MANENO_SPACE.sidebarWidth};
    flex: 1;
    min-height: calc(100vh - ${MANENO_SPACE.headerHeight});
    background: var(--color-page-bg);
    overflow-x: hidden;
    padding: ${MANENO_SPACE.pagePadding};
  }

  .maneno-content > * {
    max-width: ${MANENO_SPACE.maxWidth};
  }

  /* ══════════════════════════════════════════════════════════
     GLOBAL CSS OVERRIDES
     Neutralise main.css children's-app styles so every
     Maneno page inherits a clean admin baseline.
     ══════════════════════════════════════════════════════════ */

  .maneno-content {
    font-family: var(--font-body);
    color: var(--color-ink);
    line-height: 1.45;

    /* ── Typography ── */
    h1 {
      font-family: var(--font-body);
      font-size: ${MANENO_TYPE.pageTitle};
      font-weight: ${MANENO_TYPE.weightBold};
      color: var(--color-ink);
      line-height: 1.3;
      margin: 0;
    }

    h2 {
      font-family: var(--font-body);
      font-size: ${MANENO_TYPE.sectionTitle};
      font-weight: ${MANENO_TYPE.weightSemi};
      color: var(--color-ink);
      line-height: 1.4;
      margin: 0;
    }

    h3 {
      font-family: var(--font-body);
      font-size: ${MANENO_TYPE.cardTitle};
      font-weight: ${MANENO_TYPE.weightSemi};
      color: var(--color-ink);
      line-height: 1.4;
      margin: 0;
    }

    h4,
    h5,
    h6 {
      font-family: var(--font-body);
      font-weight: ${MANENO_TYPE.weightSemi};
      color: var(--color-ink);
      margin: 0;
    }

    h4 {
      font-size: ${MANENO_TYPE.body};
    }
    h5 {
      font-size: ${MANENO_TYPE.bodySmall};
    }
    h6 {
      font-size: ${MANENO_TYPE.small};
    }

    p {
      font-size: ${MANENO_TYPE.body};
      color: var(--color-ink-muted);
      margin: 0;
      line-height: 1.5;
    }

    /* ── Page title area ── */
    .page-title {
      margin-bottom: ${MANENO_SPACE.sectionGap};

      h1 {
        font-size: ${MANENO_TYPE.pageTitle};
        font-weight: ${MANENO_TYPE.weightBold};
        font-family: var(--font-body);
        color: var(--color-ink);
      }

      p {
        font-size: ${MANENO_TYPE.body};
        color: var(--color-ink-muted);
        margin-top: 4px;
      }
    }

    .title h1 {
      font-family: var(--font-body);
      font-size: ${MANENO_TYPE.pageTitle};
      font-weight: ${MANENO_TYPE.weightBold};
      color: var(--color-ink);
    }

    /* ── Cards ── */
    .ds-card {
      background: var(--color-surface);
      border: 1px solid var(--color-border);
      border-radius: ${MANENO_RADIUS.card};
      box-shadow: ${MANENO_SHADOW.card};
      padding: 18px;
      margin-bottom: ${MANENO_SPACE.sectionGap};
    }

    .chart-card {
      background: var(--color-surface);
      border: 1px solid var(--color-border);
      border-radius: ${MANENO_RADIUS.card};
      box-shadow: ${MANENO_SHADOW.card};
      padding: 20px;
      margin-bottom: ${MANENO_SPACE.sectionGap};

      .chart-title b,
      .chart-title {
        font-family: var(--font-body);
        font-size: ${MANENO_TYPE.sectionTitle};
        font-weight: ${MANENO_TYPE.weightSemi};
        color: var(--color-ink);
      }
    }

    .chart-thumb {
      padding: 0;
    }

    .grph-top {
      margin-bottom: 16px;
    }

    /* ── Stat boxes (legacy .box pattern) ── */
    .box,
    .box.box1,
    .box.box2,
    .box.box3,
    .box.box4 {
      background: var(--color-surface);
      border: 1px solid var(--color-border);
      border-radius: ${MANENO_RADIUS.card};
      padding: 18px;
      color: var(--color-ink);
      font-family: var(--font-body);
      box-shadow: ${MANENO_SHADOW.card};

      h2 {
        font-family: var(--font-body);
        font-size: ${MANENO_TYPE.statNumber};
        font-weight: ${MANENO_TYPE.weightBold};
        color: var(--color-ink);
        line-height: 1.2;
      }

      p {
        font-size: ${MANENO_TYPE.small};
        color: var(--color-ink-muted);
      }

      .box-border {
        border: none;
        border-radius: 0;
        margin: 0;
      }

      .icon {
        margin-bottom: 0;
        margin-right: 12px;
      }
    }

    /* ── Buttons ── */
    .btn {
      font-family: var(--font-body);
      font-size: ${MANENO_TYPE.body};
      font-weight: ${MANENO_TYPE.weightMd};
      border-radius: ${MANENO_RADIUS.button};
      padding: 7px 14px;
      transition: all 0.15s;
    }

    .custom_primary_btn,
    .btn-primary {
      background: var(--color-btn-primary-bg);
      border-color: var(--color-btn-primary-bg);
      color: var(--color-btn-primary-text);
      font-weight: ${MANENO_TYPE.weightSemi};
      border-radius: ${MANENO_RADIUS.button};

      &:hover {
        background: var(--color-btn-primary-hover);
        border-color: var(--color-btn-primary-hover);
      }
    }

    /* ── Tables ── */
    table {
      font-family: var(--font-body);
      font-size: ${MANENO_TYPE.body};
    }

    th {
      font-weight: ${MANENO_TYPE.weightSemi};
      font-size: ${MANENO_TYPE.small};
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: var(--color-ink-muted);
      padding: 10px 12px;
    }

    td {
      padding: 10px 12px;
      color: var(--color-ink);
      border-color: var(--color-border-light);
    }

    /* ── Tabs ── */
    .nav-tabs {
      border-bottom: 1px solid var(--color-border);
      gap: 0;

      .nav-link {
        font-family: var(--font-body);
        font-size: ${MANENO_TYPE.body};
        font-weight: ${MANENO_TYPE.weightMd};
        color: var(--color-ink-muted);
        padding: 8px 14px;
        border: none;
        border-bottom: 2px solid transparent;
        border-radius: 0;
        transition: color 0.15s, border-color 0.15s;

        &:hover {
          color: var(--color-ink);
          border-bottom-color: var(--color-border);
        }

        &.active {
          color: var(--color-primary);
          font-weight: ${MANENO_TYPE.weightSemi};
          border-bottom-color: var(--color-primary);
          background: transparent;
        }
      }
    }

    .tab-content {
      padding-top: 20px;
    }

    /* ── Breadcrumbs ── */
    .main_breadcrumb,
    .breadcrumb {
      font-size: ${MANENO_TYPE.small};
      color: var(--color-ink-muted);
      padding: 0;
      margin-bottom: 16px;
      background: transparent;

      .breadcrumb-item {
        font-size: ${MANENO_TYPE.small};

        a {
          color: var(--color-primary);
          text-decoration: none;

          &:hover {
            text-decoration: underline;
          }
        }

        &.active {
          color: var(--color-ink-muted);
        }
      }
    }

    /* ── Forms ── */
    .form-control {
      font-family: var(--font-body);
      font-size: ${MANENO_TYPE.body};
      border: 1px solid var(--color-border);
      border-radius: ${MANENO_RADIUS.button};
      padding: 7px 10px;
      color: var(--color-ink);
      transition: border-color 0.15s;

      &:focus {
        border-color: var(--color-primary);
        box-shadow: 0 0 0 3px rgba(22, 91, 149, 0.1);
        outline: none;
      }
    }

    label {
      font-size: ${MANENO_TYPE.bodySmall};
      font-weight: ${MANENO_TYPE.weightMd};
      color: var(--color-ink);
      margin-bottom: 4px;
    }

    /* ── Filters / search ── */
    .sec_body {
      background: var(--color-surface);
      border: 1px solid var(--color-border);
      border-radius: ${MANENO_RADIUS.card};
      padding: 16px;
      margin-bottom: ${MANENO_SPACE.sectionGap};
    }

    /* ── Cards used across pages ── */
    .card {
      border: 1px solid var(--color-border);
      border-radius: ${MANENO_RADIUS.card};
      box-shadow: ${MANENO_SHADOW.card};
    }

    /* ── Assignments page ── */
    .assignments-page .assignmentsTableWrapper {
      background: var(--color-surface);
      border: 1px solid var(--color-border);
      border-radius: ${MANENO_RADIUS.card};
      padding: 16px;
    }

    /* ── Pro text / subtitles ── */
    .pro_text {
      font-family: var(--font-body);
      font-size: ${MANENO_TYPE.body};
      color: var(--color-ink-muted);
    }

    /* ── Class analysis cards ── */
    .class_analysis {
      .ana_card {
        background: var(--color-surface);
        border: 1px solid var(--color-border);
        border-radius: ${MANENO_RADIUS.card};
        box-shadow: ${MANENO_SHADOW.card};
        padding: 16px;
      }

      .ana_title {
        font-size: ${MANENO_TYPE.bodySmall};
        font-weight: ${MANENO_TYPE.weightMd};
        color: var(--color-ink-muted);
      }

      .ana_number {
        font-size: ${MANENO_TYPE.statNumber};
        font-weight: ${MANENO_TYPE.weightBold};
        color: var(--color-ink);
        font-family: var(--font-body);
      }

      .ana_range {
        font-size: ${MANENO_TYPE.xsmall};
        color: var(--color-ink-faint);
      }
    }

    /* ── Row/Col bootstrap overrides ── */
    .row {
      --bs-gutter-x: ${MANENO_SPACE.cardGap};
      --bs-gutter-y: ${MANENO_SPACE.cardGap};
    }
  }

  /* ── Tablet (<=1024px) ─────────────────────────────────── */
  @media (max-width: 1024px) {
    .header-brand {
      width: 72px;
      padding: 0;
      justify-content: center;
    }

    .maneno-logo-text {
      display: none;
    }

    .maneno-sidebar {
      width: 72px;
      padding: 12px 0;
    }

    .sidebar-section-label {
      display: none;
    }

    .sidebar-nav {
      padding: 0 6px;
    }

    .sidebar-nav-item {
      justify-content: center;
      padding: 10px;
      gap: 0;

      .nav-label {
        display: none;
      }

      svg,
      img {
        width: 22px;
        height: 22px;
        opacity: 0.8;
      }
    }

    .sidebar-nav-group-trigger {
      justify-content: center;
      padding: 10px;
      gap: 0;

      .nav-label,
      .nav-chevron {
        display: none;
      }

      svg,
      img {
        width: 22px;
        height: 22px;
        opacity: 0.8;
      }
    }

    .sidebar-submenu {
      display: none;
    }

    .sidebar-footer {
      padding: 0 6px;
    }

    .maneno-content {
      margin-left: 72px;
      padding: 20px;
    }
  }

  /* ── Mobile (<=768px) ──────────────────────────────────── */
  @media (max-width: 768px) {
    .header-brand {
      width: 60px;
    }

    .maneno-header {
      padding: 0 16px 0 0;
    }

    .maneno-body {
      padding-bottom: 64px;
      flex-direction: column;
    }

    .maneno-sidebar {
      top: auto;
      bottom: 0;
      left: 0;
      right: 0;
      width: 100%;
      height: 64px;
      flex-direction: row;
      align-items: center;
      padding: 0;
      z-index: 200;
      overflow: hidden;
    }

    .sidebar-section-label,
    .sidebar-divider,
    .sidebar-footer {
      display: none;
    }

    .sidebar-nav {
      flex-direction: row;
      flex: 1;
      padding: 0;
      gap: 0;
      justify-content: space-around;
      align-items: center;
      height: 100%;
    }

    .sidebar-nav-item {
      flex-direction: column;
      gap: 3px;
      padding: 6px 8px;
      border-radius: 0;
      flex: 1;
      height: 100%;
      justify-content: center;

      &::before {
        display: none;
      }

      .nav-label {
        display: block;
        font-size: 10px;
      }

      svg,
      img {
        width: 20px;
        height: 20px;
      }

      &.active {
        background: none;

        .nav-label {
          color: var(--color-primary);
        }

        svg {
          color: var(--color-primary);
        }

        img {
          filter: none;
          opacity: 1;
        }
      }
    }

    .sidebar-nav-group {
      flex: 1;
      height: 100%;
    }

    .sidebar-nav-group-trigger {
      flex-direction: column;
      gap: 3px;
      padding: 6px 8px;
      border-radius: 0;
      height: 100%;
      justify-content: center;

      .nav-label {
        display: block;
        font-size: 10px;
      }

      .nav-chevron {
        display: none;
      }

      svg,
      img {
        width: 20px;
        height: 20px;
      }
    }

    .sidebar-submenu {
      display: none;
    }

    .maneno-content {
      margin-left: 0;
      padding: 16px;
    }
  }
`;
